import { useCallback, useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Users as UsersIcon, Archive, ArchiveRestore, Pencil } from "lucide-react";

const ROLES = ["customer", "field_worker", "property_manager", "account_admin", "super_admin"];
const PREFIXES = ["Mr.", "Mrs.", "Ms.", "Dr."];
const MARITAL = ["married", "unmarried"];
const CYCLES = ["monthly", "quarterly", "half_yearly", "yearly"];

const EMPTY_NEW = { name: "", email: "", password: "", role: "customer" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);   // user being edited
  const [form, setForm] = useState(EMPTY_NEW);
  const [profile, setProfile] = useState({});

  const load = useCallback(async () => {
    const [u, fd, pr] = await Promise.all([
      api.get("/users"), api.get("/field-definitions"), api.get("/projects"),
    ]);
    setUsers(u.data); setFieldDefs(fd.data); setProjects(pr.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (u) => {
    setEdit(u);
    setProfile({
      name: u.name || "", prefix: u.prefix || "", mobile: u.mobile || "",
      address: u.address || "", dob: u.dob || "", pan: u.pan || "",
      aadhaar: u.aadhaar || "", marital_status: u.marital_status || "",
      billing_cycle: u.billing_cycle || "", custom: u.custom || {},
      common_maintenance: !!u.common_maintenance,
      common_project_id: u.common_project_id || "",
      common_area_sqft: u.common_area_sqft || "",
      payment_plan_until: u.payment_plan_until || "",
      payment_plan_note: u.payment_plan_note || "",
    });
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm(EMPTY_NEW); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...profile,
        common_maintenance: !!profile.common_maintenance,
        common_project_id: profile.common_maintenance ? (profile.common_project_id || null) : null,
        common_area_sqft: profile.common_maintenance ? Number(profile.common_area_sqft || 0) : null,
        payment_plan_until: profile.payment_plan_until || null,
        payment_plan_note: profile.payment_plan_until ? (profile.payment_plan_note || null) : null,
      };
      await api.patch(`/users/${edit.id}/profile`, payload);
      toast.success("Profile updated"); setEdit(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const toggleArchive = async (u) => {
    try {
      await api.patch(`/users/${u.id}/${u.is_archived ? "unarchive" : "archive"}`);
      toast.success(u.is_archived ? "Restored" : "Archived");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const visible = users.filter((u) => showArchived ? true : !u.is_archived);

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3"><UsersIcon className="text-primary" /><h1 className="heading text-3xl md:text-4xl">Users</h1></div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground flex items-center gap-2 mr-2">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived-toggle" />
            Show archived
          </label>
          <Button onClick={() => setOpen(true)} data-testid="new-user-btn"><Plus size={14} className="mr-2" />New user</Button>
        </div>
      </div>

      <div className="border border-border rounded-lg bg-card divide-y divide-border">
        {visible.map((u) => (
          <div key={u.id} className={`p-4 flex items-center justify-between gap-3 ${u.is_archived ? "opacity-60" : ""}`} data-testid={`user-${u.email}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{u.prefix ? `${u.prefix} ` : ""}{u.name}{u.is_archived ? " · archived" : ""}</div>
              <div className="text-xs text-muted-foreground">{u.email}{u.mobile ? ` · ${u.mobile}` : ""}{u.billing_cycle ? ` · ${u.billing_cycle}` : ""}</div>
            </div>
            <span className="text-[10px] uppercase tracking-widest bg-secondary text-secondary-foreground rounded px-2 py-1">{u.role.replace("_", " ")}</span>
            <Button variant="ghost" size="sm" onClick={() => startEdit(u)} data-testid={`edit-${u.email}`}><Pencil size={13} /></Button>
            <Button variant="ghost" size="sm" onClick={() => toggleArchive(u)} data-testid={`archive-${u.email}`}>
              {u.is_archived ? <ArchiveRestore size={13} /> : <Archive size={13} />}
            </Button>
          </div>
        ))}
      </div>

      {/* Create modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div><Label>Password</Label><Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => (<SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <DialogFooter><Button type="submit" data-testid="user-save-btn">Create</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit-profile modal */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl" data-testid="profile-modal">
          <DialogHeader><DialogTitle>Edit profile · {edit?.email}</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={saveProfile} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Prefix</Label>
                  <Select value={profile.prefix} onValueChange={(v) => setProfile({ ...profile, prefix: v })}>
                    <SelectTrigger data-testid="pf-prefix"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{PREFIXES.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Full name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="pf-name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Mobile</Label><Input value={profile.mobile} onChange={(e) => setProfile({ ...profile, mobile: e.target.value })} data-testid="pf-mobile" /></div>
                <div><Label>DOB</Label><Input type="date" value={profile.dob} onChange={(e) => setProfile({ ...profile, dob: e.target.value })} data-testid="pf-dob" /></div>
              </div>
              <div><Label>Address</Label><Textarea rows={2} value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} data-testid="pf-address" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>PAN</Label><Input value={profile.pan} onChange={(e) => setProfile({ ...profile, pan: e.target.value.toUpperCase() })} data-testid="pf-pan" /></div>
                <div><Label>Aadhaar</Label><Input value={profile.aadhaar} onChange={(e) => setProfile({ ...profile, aadhaar: e.target.value })} data-testid="pf-aadhaar" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Marital status</Label>
                  <Select value={profile.marital_status} onValueChange={(v) => setProfile({ ...profile, marital_status: v })}>
                    <SelectTrigger data-testid="pf-marital"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{MARITAL.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Billing cycle</Label>
                  <Select value={profile.billing_cycle} onValueChange={(v) => setProfile({ ...profile, billing_cycle: v })}>
                    <SelectTrigger data-testid="pf-cycle"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{CYCLES.map((c) => (<SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="pt-3 border-t border-border">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">Payment plan</div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <Label>Pause escalations until</Label>
                    <Input type="date" value={profile.payment_plan_until || ""}
                           onChange={(e) => setProfile({ ...profile, payment_plan_until: e.target.value })}
                           data-testid="pf-plan-until" />
                  </div>
                  {profile.payment_plan_until && (
                    <Button type="button" variant="ghost" size="sm"
                            onClick={() => setProfile({ ...profile, payment_plan_until: "", payment_plan_note: "" })}
                            data-testid="pf-plan-clear">Clear plan</Button>
                  )}
                </div>
                {profile.payment_plan_until && (
                  <div className="mt-3">
                    <Label>Why? <span className="text-muted-foreground font-normal text-[11px]">(admin note)</span></Label>
                    <Input placeholder="e.g. school fees Feb-Apr"
                           value={profile.payment_plan_note || ""}
                           onChange={(e) => setProfile({ ...profile, payment_plan_note: e.target.value })}
                           data-testid="pf-plan-note" />
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">While this date is in the future, bulk reminders and 60-day escalations skip this owner. Set to blank when the plan ends.</p>
              </div>
              <div className="pt-3 border-t border-border">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">Common maintenance</div>
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1"
                         checked={!!profile.common_maintenance}
                         onChange={(e) => setProfile({ ...profile, common_maintenance: e.target.checked })}
                         data-testid="pf-common-maint-toggle" />
                  <span>
                    <span className="font-medium">No plot — bill common-area maintenance</span>
                    <span className="block text-xs text-muted-foreground">Applies the 50% maintenance formula (rate × common area). Used for owners who don't hold a plot yet still share upkeep.</span>
                  </span>
                </label>
                {profile.common_maintenance && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label>Project</Label>
                      <Select value={profile.common_project_id || ""}
                              onValueChange={(v) => setProfile({ ...profile, common_project_id: v })}>
                        <SelectTrigger data-testid="pf-common-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                        <SelectContent>{projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Common area (sq ft)</Label>
                      <Input type="number" min="0" step="1"
                             value={profile.common_area_sqft || ""}
                             onChange={(e) => setProfile({ ...profile, common_area_sqft: e.target.value })}
                             data-testid="pf-common-sqft" />
                    </div>
                  </div>
                )}
              </div>
              {fieldDefs.length > 0 && (
                <div className="pt-3 border-t border-border">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">Custom fields</div>
                  <div className="grid grid-cols-2 gap-3">
                    {fieldDefs.map((f) => (
                      <div key={f.key}>
                        <Label>{f.label}{f.required ? " *" : ""}</Label>
                        {f.type === "select" ? (
                          <Select value={profile.custom?.[f.key] || ""}
                                  onValueChange={(v) => setProfile({ ...profile, custom: { ...profile.custom, [f.key]: v } })}>
                            <SelectTrigger data-testid={`pf-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>{(f.options || []).map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                          </Select>
                        ) : (
                          <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                                 value={profile.custom?.[f.key] || ""}
                                 onChange={(e) => setProfile({ ...profile, custom: { ...profile.custom, [f.key]: e.target.value } })}
                                 data-testid={`pf-${f.key}`} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
                <Button type="submit" data-testid="pf-save-btn">Save profile</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
